import { gatewayApiAgent } from 'common/api/apiAgents';
import { CountryCode } from 'common/api-types/common';

import {
  BulkOperation,
  BulkOperationList,
  ConfirmForecastOverrideRequest,
  ListMetaOverrides,
  ListOverrideDraftSummary,
  OverridesDraft,
} from './api-types';
import { FetchOverridesListParams } from './types';

export async function fetchOverridesList(params: FetchOverridesListParams) {
  const response = await gatewayApiAgent.get<ListMetaOverrides>(
    `/demand-forecast/public/v1/forecast-overrides`,
    {
      params,
    },
  );
  return response.data;
}

export async function downloadOverrideExample() {
  const response = await gatewayApiAgent.get<Blob>(
    `/demand-forecast/public/v1/forecast-overrides/example-csv`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function downloadCurrentOverrides(countryCode: CountryCode) {
  const response = await gatewayApiAgent.get<Blob>(
    `/demand-forecast/public/v1/forecast-overrides/${countryCode}/csv`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
}

export async function uploadOverrideCsv(countryCode: CountryCode, file: File) {
  const response = await gatewayApiAgent.post<OverridesDraft>(
    `/demand-forecast/public/v1/forecast-overrides/${countryCode}/drafts`,
    file,
    {
      headers: { 'Content-Type': 'text/csv' },
    },
  );

  return response.data;
}

export async function fetchOverrideDraftSummary(
  draftId: OverridesDraft['id'],
  countryCode: CountryCode,
) {
  const response = await gatewayApiAgent.get<ListOverrideDraftSummary>(
    `/demand-forecast/public/v1/forecast-overrides/${countryCode}/drafts/${draftId}/summary`,
  );
  return response.data;
}

export async function confirmOverridesDraft(
  draftId: OverridesDraft['id'],
  countryCode: CountryCode,
  request: ConfirmForecastOverrideRequest,
) {
  const response = await gatewayApiAgent.post<void>(
    `/demand-forecast/public/v2/forecast-overrides/${countryCode}/drafts/${draftId}/confirm`,
    request,
  );

  return response.data;
}

// So far the endpoint is only used for forecast overrides, so just adding the bulkOperationType for now.
// When we know more how it's used for other types, we'll look into making this api function shared and generic.
export async function bulkUploadOverrideCsv(
  countryCode: CountryCode,
  file: File,
  deleteExisting: boolean,
) {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const encodedFileName = encodeURIComponent(sanitizedFileName);

  const response = await gatewayApiAgent.post<BulkOperation>(
    `/demand-forecast/public/v1/bulk-operations/import`,
    file,
    {
      params: {
        countryCode,
        bulkOperationType: 'FORECAST_OVERRIDES',
        deleteExisting,
      },
      headers: {
        'Content-Type': 'text/csv',
        'Content-Disposition': `attachment; filename="${sanitizedFileName}"; filename*=UTF-8''${encodedFileName}`,
      },
    },
  );

  return response.data;
}

export async function fetchBulkOperations(countryCode: CountryCode) {
  const response = await gatewayApiAgent.get<BulkOperationList>(
    `/demand-forecast/public/v1/bulk-operations`,
    {
      params: {
        countryCode,
        bulkOperationType: 'FORECAST_OVERRIDES',
      },
    },
  );
  return response.data;
}

export async function fetchSingleOperation(id: string) {
  const response = await gatewayApiAgent.get<BulkOperation>(
    `/demand-forecast/public/v1/bulk-operations/${id}`,
  );
  return response.data;
}

export async function downloadBulkOperationReport(id: string) {
  const response = await gatewayApiAgent.get<Blob>(
    `/demand-forecast/public/v1/bulk-operations/${id}/report`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
}

export async function bulkUploadDeleteOverridesCsv(countryCode: CountryCode, file: File) {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const encodedFileName = encodeURIComponent(file.name);

  const response = await gatewayApiAgent.post<void>(
    `/demand-forecast/public/v1/bulk-operations/delete`,
    file,
    {
      params: {
        countryCode,
        bulkOperationType: 'FORECAST_OVERRIDES',
      },
      headers: {
        'Content-Type': 'text/csv',
        'Content-Disposition': `attachment; filename="${sanitizedFileName}"; filename*=UTF-8''${encodedFileName}`,
      },
    },
  );
  return response.data;
}

export async function downloadDeleteOverridesExample() {
  const response = await gatewayApiAgent.get<Blob>(
    `/demand-forecast/public/v1/bulk-operations/delete-example-csv`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
}
